/**
 * The operator's session, held where every request can reach it.
 *
 * The API authenticates a browser exactly one way. `resolvePrincipal`
 * (`apps/api/src/principal.ts:23`) returns nothing at all unless the request
 * carries `Authorization: Bearer …`; a cookie on its own authenticates no route.
 * Signing in therefore means keeping the bearer the API hands back in
 * `set-auth-token` and presenting it on every request afterwards.
 *
 * There is no session-read route to ask "am I still signed in" — the product mounts
 * sign-in and sign-out and nothing else (`apps/api/src/app.ts:211-218`), on purpose.
 * So a stored bearer is a claim, not proof: it is tested by the first request that
 * needs it, and a 401 anywhere retires it (`client.ts`). That is why this module
 * keeps the operator's identity beside the token — it is what the sign-in response
 * said, kept so the shell can name who is signed in without inventing a route.
 *
 * This module imports nothing. Everything that needs the session — the client that
 * attaches the header, the sign-in screen, the shell — reads it from here, which is
 * what keeps the client free of a cycle back through sign-in.
 */

export interface OperatorSession {
  token: string;
  userId: string;
  email: string;
  name: string;
}

export const SESSION_STORAGE_KEY = "radio.session";

type Listener = () => void;

const listeners = new Set<Listener>();
let current: OperatorSession | null = null;
let hydrated = false;

/**
 * Storage is not always there. A hardened context refuses it outright, and some
 * runtimes expose no `localStorage` at all — in either case the session lives for
 * this page and no longer, which is a worse experience and not a broken one.
 */
function storage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return (window.localStorage as Storage | undefined | null) ?? null;
  } catch {
    return null;
  }
}

/** Every field is checked: a half-written or hand-edited entry is no session. */
export function parseSession(raw: string | null): OperatorSession | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { token, userId, email, name } = value as Record<string, unknown>;
    if (typeof token !== "string" || token === "") return null;
    if (typeof userId !== "string" || userId === "") return null;
    return {
      token,
      userId,
      email: typeof email === "string" ? email : "",
      name: typeof name === "string" ? name : "",
    };
  } catch {
    return null;
  }
}

export function readSession(): OperatorSession | null {
  if (!hydrated) {
    current = parseSession(storage()?.getItem(SESSION_STORAGE_KEY) ?? null);
    hydrated = true;
  }
  return current;
}

export function sessionToken(): string | null {
  return readSession()?.token ?? null;
}

function publish(next: OperatorSession | null): void {
  current = next;
  hydrated = true;
  for (const listener of listeners) listener();
}

export function storeSession(session: OperatorSession): void {
  try {
    storage()?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // A refused storage costs the operator his session on reload and nothing more.
  }
  publish(session);
}

export function clearSession(): void {
  try {
    storage()?.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to do: the in-memory session is dropped either way.
  }
  publish(null);
}

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Only for tests, which need a store that does not remember the last one. */
export function resetSessionForTest(): void {
  current = null;
  hydrated = false;
}
